import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${props => props.$isOpen ? '100%' : '0'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  transition: all 0.3s ease;
  overflow: hidden;
  
  @media (min-width: 768px) {
    max-width: ${props => props.$isOpen ? '20rem' : '0'};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 2.5rem;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.foreground};
  font-size: 0.9375rem;
  outline: none;
  transition: all 0.2s;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    padding: 0.625rem 2.75rem 0.625rem 2.75rem;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.mutedForeground};
  }
  
  &:focus {
    border-color: ${props => props.theme.colors.ring || props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.ring || props.theme.colors.primary}33;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.mutedForeground};
  pointer-events: none;
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    
    @media (min-width: 1024px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.mutedForeground};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.colors.foreground};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SearchBar = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setQuery('');
      onClose();
    }
  };

  const handleClear = () => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <SearchContainer $isOpen={isOpen}>
      <form onSubmit={handleSubmit}>
        <SearchIcon>
          <Search />
        </SearchIcon>
        <SearchInput
          ref={inputRef}
          type="text"
          placeholder={t('search.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <ClearButton type="button" onClick={handleClear} aria-label={t('common.close')}>
            <X />
          </ClearButton>
        )}
      </form>
    </SearchContainer>
  );
};

export default SearchBar;



